import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SleepPlan, DayActivity } from "@/types/sleep-plan";
import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Trash2 } from "lucide-react";

interface EditSleepPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sleepPlan: SleepPlan | null;
  onSave: (updatedPlan: SleepPlan) => Promise<void>;
}

export function EditSleepPlanDialog({
  open,
  onOpenChange,
  sleepPlan,
  onSave,
}: EditSleepPlanDialogProps) {
  const [editedPlan, setEditedPlan] = useState<SleepPlan | null>(sleepPlan);
  const [saving, setSaving] = useState(false);

  if (!editedPlan?.plan_data) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedPlan);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const updateScheduleActivity = (index: number, field: keyof DayActivity, value: string) => {
    const newActivities = [...editedPlan.plan_data.day_schedule.example_day];
    newActivities[index] = { ...newActivities[index], [field]: value };
    setEditedPlan({
      ...editedPlan,
      plan_data: {
        ...editedPlan.plan_data,
        day_schedule: {
          ...editedPlan.plan_data.day_schedule,
          example_day: newActivities,
        },
      },
    });
  };

  const addScheduleActivity = () => {
    const newActivities = [
      ...editedPlan.plan_data.day_schedule.example_day,
      { time: "00:00", activity: "New Activity", notes: "" },
    ];
    setEditedPlan({
      ...editedPlan,
      plan_data: {
        ...editedPlan.plan_data,
        day_schedule: {
          ...editedPlan.plan_data.day_schedule,
          example_day: newActivities,
        },
      },
    });
  };

  const removeScheduleActivity = (index: number) => {
    const newActivities = editedPlan.plan_data.day_schedule.example_day.filter(
      (_, i) => i !== index
    );
    setEditedPlan({
      ...editedPlan,
      plan_data: {
        ...editedPlan.plan_data,
        day_schedule: {
          ...editedPlan.plan_data.day_schedule,
          example_day: newActivities,
        },
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Edit Sleep Plan</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Opening Message */}
            <div className="space-y-2">
              <Label>Opening Message</Label>
              <Textarea
                value={editedPlan.plan_data.personalized_opening}
                onChange={(e) =>
                  setEditedPlan({
                    ...editedPlan,
                    plan_data: {
                      ...editedPlan.plan_data,
                      personalized_opening: e.target.value,
                    },
                  })
                }
                rows={3}
              />
            </div>

            {/* Summary */}
            <div className="space-y-2">
              <Label>Summary</Label>
              <Textarea
                value={editedPlan.plan_data.summary}
                onChange={(e) =>
                  setEditedPlan({
                    ...editedPlan,
                    plan_data: {
                      ...editedPlan.plan_data,
                      summary: e.target.value,
                    },
                  })
                }
                rows={3}
              />
            </div>

            {/* Daily Schedule */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Daily Schedule</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addScheduleActivity}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Activity
                </Button>
              </div>

              {editedPlan.plan_data.day_schedule.example_day.map((activity, idx) => (
                <div key={idx} className="flex gap-2 items-start p-3 rounded-lg bg-accent/50">
                  <div className="flex-1 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Time</Label>
                        <Input
                          value={activity.time}
                          onChange={(e) =>
                            updateScheduleActivity(idx, "time", e.target.value)
                          }
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Activity</Label>
                        <Input
                          value={activity.activity}
                          onChange={(e) =>
                            updateScheduleActivity(idx, "activity", e.target.value)
                          }
                          className="h-8"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Input
                        value={activity.notes || ""}
                        onChange={(e) =>
                          updateScheduleActivity(idx, "notes", e.target.value)
                        }
                        className="h-8"
                        placeholder="Optional notes"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeScheduleActivity(idx)}
                    className="mt-6"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}

              <div className="space-y-2">
                <Label>Personalized Note</Label>
                <Textarea
                  value={editedPlan.plan_data.day_schedule.personalized_note}
                  onChange={(e) =>
                    setEditedPlan({
                      ...editedPlan,
                      plan_data: {
                        ...editedPlan.plan_data,
                        day_schedule: {
                          ...editedPlan.plan_data.day_schedule,
                          personalized_note: e.target.value,
                        },
                      },
                    })
                  }
                  rows={2}
                />
              </div>
            </div>

            {/* Closing Message */}
            <div className="space-y-2">
              <Label>Closing Message</Label>
              <Textarea
                value={editedPlan.plan_data.closing_message}
                onChange={(e) =>
                  setEditedPlan({
                    ...editedPlan,
                    plan_data: {
                      ...editedPlan.plan_data,
                      closing_message: e.target.value,
                    },
                  })
                }
                rows={3}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
